import React, { useEffect, useState, Component } from 'react';
import { Loader2, PlusCircleIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon, HandshakeIcon } from 'lucide-react';
import { FormSection, TextField, TextAreaField, FileUploadField, SelectField, DisplayField } from '../FormComponents';
import { StatusBadge } from '../StatusBadge';
// Mock RFP/RFQ suppliers that would come from the previous step
const mockRFPSuppliers = [{
  id: 'SUP-001',
  name: 'TechCorp Solutions',
  quotedAmount: 45000,
  deliveryTimeline: '4 Weeks',
  technicalScore: 85,
  financialScore: 90,
  totalScore: 87.5
}, {
  id: 'SUP-002',
  name: 'Global Logistics Inc',
  quotedAmount: 52000,
  deliveryTimeline: '3 Weeks',
  technicalScore: 78,
  financialScore: 82,
  totalScore: 80.0
}, {
  id: 'SUP-003',
  name: 'BuildIt Construction',
  quotedAmount: 48500,
  deliveryTimeline: '5 Weeks',
  technicalScore: 80,
  financialScore: 85,
  totalScore: 82.5
}];
interface NegotiationRecord {
  supplierId: string;
  supplierName: string;
  originalAmount: number;
  negotiatedAmount: string;
  remarks: string;
}
interface NOAPanelProps {
  referenceType?: 'RFP' | 'RFQ';
  onStatusChange?: (status: string) => void;
}
export function NOAPanel({
  referenceType = 'RFP',
  onStatusChange
}: NOAPanelProps) {
  const [formData, setFormData] = useState({
    awardedSupplier: '',
    finalAmount: '',
    remarks: '',
    status: 'Draft'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReadOnly = formData.status !== 'Draft';
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(formData.status);
    }
  }, [formData.status, onStatusChange]);
  // When awarded supplier changes, auto-fill final amount from quoted amount
  useEffect(() => {
    if (!formData.awardedSupplier) return;
    const supplier = mockRFPSuppliers.find(s => s.id === formData.awardedSupplier);
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        finalAmount: supplier.quotedAmount.toString()
      }));
    }
  }, [formData.awardedSupplier]);
  // Get supplier details for the awarded supplier
  const awardedSupplierData = mockRFPSuppliers.find(s => s.id === formData.awardedSupplier);
  const finalAmount = formData.finalAmount ? Number(formData.finalAmount) : 0;
  // Derive contact info from awarded supplier
  const contactInfo = awardedSupplierData ? {
    contactPerson: `${awardedSupplierData.name} Rep`,
    email: `contact@${awardedSupplierData.name.toLowerCase().replace(/\s+/g, '')}.com`,
    phone: '+1 (555) 000-0000',
    address: '123 Business Ave, Suite 100'
  } : null;
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        status: 'Pending Approval'
      }));
      setIsSubmitting(false);
    }, 1000);
  };
  const handleIssue = () => {
    setFormData(prev => ({
      ...prev,
      status: 'Issued'
    }));
  };
  return <div className="space-y-6" data-id="element-291">
      {/* NOA Header */}
      <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-292">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200" data-id="element-293">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-294">
            Notice of Award (NOA)
          </h3>
          <div className="flex items-center space-x-4" data-id="element-295">
            <DisplayField label={`${referenceType} Reference No`} value={`${referenceType}-2023-001`} data-id="element-296" />
          </div>
        </div>

        {/* ===== NOA AWARD SECTION ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-297">
          {/* Supplier Selection & Info */}
          <div data-id="element-298">
            <SelectField label="Award Supplier" value={formData.awardedSupplier} onChange={e => setFormData({
            ...formData,
            awardedSupplier: e.target.value
          })} options={[{
            value: '',
            label: 'Select supplier to award...'
          }, ...mockRFPSuppliers.map(s => ({
            value: s.id,
            label: `${s.name} (Score: ${s.totalScore})`
          }))]} required className="mb-6" disabled={isReadOnly} data-id="element-299" />

            {contactInfo && <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-4" data-id="element-300">
                <h4 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-301">
                  Supplier Information
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-302">
                  <DisplayField label="Name" value={awardedSupplierData?.name || ''} data-id="element-303" />
                  <DisplayField label="Contact Person" value={contactInfo.contactPerson} data-id="element-304" />
                  <DisplayField label="Email" value={contactInfo.email} data-id="element-305" />
                  <DisplayField label="Phone" value={contactInfo.phone} data-id="element-306" />
                  <div className="col-span-2" data-id="element-307">
                    <DisplayField label="Address" value={contactInfo.address} data-id="element-308" />
                  </div>
                </div>
              </div>}
          </div>

          {/* RFP/RFQ Details & Scoring */}
          {awardedSupplierData && <div className="space-y-6" data-id="element-309">
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 space-y-4" data-id="element-310">
                <h4 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-311">
                  {referenceType} Submitted Information
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-312">
                  <DisplayField label="Quoted Amount" value={`$${awardedSupplierData.quotedAmount.toLocaleString()}`} data-id="element-313" />
                  <DisplayField label="Delivery Timeline" value={awardedSupplierData.deliveryTimeline} data-id="element-314" />
                  <DisplayField label="Technical Score" value={awardedSupplierData.technicalScore} data-id="element-315" />
                  <DisplayField label="Financial Score" value={awardedSupplierData.financialScore} data-id="element-316" />
                  <div className="col-span-2 bg-indigo-50 p-3 rounded-md flex justify-between items-center" data-id="element-317">
                    <span className="text-sm font-medium text-indigo-900" data-id="element-318">
                      Total Evaluation Score
                    </span>
                    <span className="text-lg font-bold text-indigo-700" data-id="element-319">
                      {awardedSupplierData.totalScore}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Financial Details */}
        <FormSection title="Final Award Amount" data-id="element-320">
          {awardedSupplierData && <DisplayField label={`${referenceType} Quoted Amount`} value={`$${awardedSupplierData.quotedAmount.toLocaleString()}`} data-id="element-321" />}
          <TextField label="Final NOA Amount" type="number" value={formData.finalAmount} onChange={e => setFormData({
          ...formData,
          finalAmount: e.target.value
        })} placeholder="Enter final award amount" disabled={isReadOnly} required data-id="element-322" />
          {finalAmount > 0 && <div className="md:col-span-2 bg-green-50 p-4 rounded-md border border-green-200 flex justify-between items-center" data-id="element-323">
              <span className="text-base font-medium text-green-900" data-id="element-324">
                Final NOA Amount
              </span>
              <span className="text-2xl font-bold text-green-700" data-id="element-325">
                ${finalAmount.toLocaleString()}
              </span>
            </div>}
        </FormSection>

        {/* Attachments & Remarks */}
        <FormSection title="Additional Information" data-id="element-326">
          <FileUploadField label="NOA Attachment" disabled={isReadOnly} data-id="element-327" />
          <div className="md:col-span-2" data-id="element-328">
            <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={3} placeholder="Add any additional notes, conditions, or instructions" disabled={isReadOnly} data-id="element-329" />
          </div>
        </FormSection>

        {/* Status & Actions */}
        <div className="mt-8 pt-5 border-t border-gray-200 flex items-center justify-between" data-id="element-330">
          <div className="flex items-center" data-id="element-331">
            <span className="text-sm font-medium text-gray-500 mr-2" data-id="element-332">
              Status:
            </span>
            <StatusBadge status={formData.status} data-id="element-333" />
          </div>
          <div className="flex space-x-3" data-id="element-334">
            {formData.status === 'Draft' && <>
                <button type="button" onClick={() => setFormData({
              ...formData,
              status: 'Draft'
            })} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-335">
                  Save as Draft
                </button>
                <button type="button" onClick={handleSubmit} disabled={isSubmitting || !formData.awardedSupplier || !formData.finalAmount} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" data-id="element-336">
                  {isSubmitting && <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" data-id="element-337" />}
                  Submit for Approval
                </button>
              </>}
            {formData.status === 'Approved' && <button type="button" onClick={handleIssue} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700" data-id="element-338">
                Issue NOA
              </button>}
          </div>
        </div>
      </div>
    </div>;
}